import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { User } from "lucide-react";

interface PatientData {
  name: string;
  date_of_birth: string;
  gender: string;
  phone: string;
  email: string;
  address: string;
  medical_history: string;
}

interface PatientInfoFormProps {
  patient: PatientData;
  setPatient: (patient: PatientData) => void;
}

export function PatientInfoForm({ patient, setPatient }: PatientInfoFormProps) {
  return (
    <Card className="shadow-md border-0 bg-white/80 dark:bg-card backdrop-blur-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <User className="w-4 h-4 text-primary" />
          </div>
          Informations personnelles
        </CardTitle>
        <CardDescription>Coordonnées et informations générales</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">Nom complet *</Label>
            <Input
              id="name"
              value={patient.name}
              onChange={(e) => setPatient({ ...patient, name: e.target.value })}
              placeholder="Jean Dupont"
              className="bg-white dark:bg-background"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="dob" className="text-sm font-medium">Date de naissance</Label>
            <Input
              id="dob"
              type="date"
              value={patient.date_of_birth}
              onChange={(e) => setPatient({ ...patient, date_of_birth: e.target.value })}
              className="bg-white dark:bg-background"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="gender" className="text-sm font-medium">Genre biologique</Label>
            <Select 
              value={patient.gender || "none"} 
              onValueChange={(value) => setPatient({ ...patient, gender: value === "none" ? "" : value })}
            >
              <SelectTrigger className="bg-white dark:bg-background">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Non renseigné</SelectItem>
                <SelectItem value="femme">Femme</SelectItem>
                <SelectItem value="homme">Homme</SelectItem>
                <SelectItem value="intersexe">Intersexe</SelectItem>
                <SelectItem value="prefere_ne_pas_repondre">Préfère ne pas répondre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm font-medium">Téléphone</Label>
            <Input
              id="phone"
              type="tel"
              value={patient.phone}
              onChange={(e) => setPatient({ ...patient, phone: e.target.value })}
              placeholder="06 12 34 56 78"
              className="bg-white dark:bg-background"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">Email</Label>
            <Input
              id="email"
              type="email"
              value={patient.email}
              onChange={(e) => setPatient({ ...patient, email: e.target.value })}
              placeholder="consultant@email.com"
              className="bg-white dark:bg-background"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address" className="text-sm font-medium">Adresse</Label>
            <Input
              id="address"
              value={patient.address}
              onChange={(e) => setPatient({ ...patient, address: e.target.value })}
              placeholder="123 rue de la Santé, Paris"
              className="bg-white dark:bg-background"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="history" className="text-sm font-medium">Antécédents médicaux</Label>
          <Textarea
            id="history"
            value={patient.medical_history}
            onChange={(e) => setPatient({ ...patient, medical_history: e.target.value })}
            placeholder="Historique médical du consultant..."
            rows={3}
            className="bg-white dark:bg-background resize-none"
          />
        </div>
      </CardContent>
    </Card>
  );
}
